import React, { useState, useEffect } from 'react';
import { useSponsorshipApi } from '../../../Hooks/useSponsorshipApi';
import CategoryFormShadcn from '../../../Components/admin/forms/CategoryFormShadcn';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '../../../ui/card';

/**
 * Admin Categories Page
 * Manages industry categories and their settings
 */
const AdminCategories = () => {
  const { categories: categoriesApi } = useSponsorshipApi();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Load categories data
  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      try {
        const response = await categoriesApi.list();
        // Handle both response formats: direct array or payload.categories
        const categoriesData = response?.payload?.categories || response?.categories || response || [];
        setCategories(categoriesData);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await categoriesApi.list();
      const categoriesData = response?.payload?.categories || response?.categories || response || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        await categoriesApi.delete(categoryId);
        loadCategories();
      } catch (error) {
        console.error('Failed to delete category:', error);
        alert('Failed to delete category: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleFormSuccess = () => {
    loadCategories();
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Industry Categories</h1>
          <p className="text-muted-foreground mt-1">Manage industry categories and their settings</p>
        </div>
        <Button onClick={handleCreateCategory}>
          Create Category
        </Button>
      </div>

      {/* Categories Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>Categories ({categories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading categories...</div>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No categories found</p>
              <Button onClick={handleCreateCategory}>
                Create First Category
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{category.name}</div>
                          {category.parent_id && (
                            <div className="text-sm text-muted-foreground">
                              Child of {category.parent_id}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground line-clamp-2">
                          {category.description || 'No description'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={category.is_active ? 'success' : 'destructive'}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(category.created_at || Date.now()).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCategory(category)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id || category._id)}
                            className="text-destructive hover:text-destructive"
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

        {/* Category Form Modal */}
        {showForm && (
          <CategoryFormShadcn
            category={editingCategory}
            onClose={handleCloseForm}
            onSuccess={handleFormSuccess}
          />
        )}
    </div>
  );
};

export default AdminCategories;
